import React, { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useHistory, useLocation } from 'react-router-dom';

import { MemberSignupResponse, useSignupUser } from '../../hooks/api/useUser';
import { ROUTE_PATH } from '../../routes/route-paths';
import { isGroupSelfEnroll } from '../../hooks/api/usePackageDetails';
import { setSessionStorageItem } from '../../hooks/utils/useSessionStorage';
import { LoginButton } from '../../components/login-button';
import { OnboardingLayout } from '../onboarding/common/layout';
import { ArrowButton } from '../../assets/images/svgs';

function KeyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <g>
        <path
          fill="rgb(var(--color-placeholder-gray))"
          fillRule="evenodd"
          d="M20.53 2.47a.75.75 0 010 1.06l-.47.47 2.47 2.47a.75.75 0 010 1.06l-3.5 3.5a.75.75 0 01-1.06 0L15.5 8.56 11.562 12.5a5.75 5.75 0 11-1.06-1.06l4.468-4.47 3.5-3.5 1-1a.75.75 0 011.06 0zM16.56 7.5l1.94 1.94L20.94 7 19 5.06 16.56 7.5zM7 11.75a4.25 4.25 0 100 8.5 4.25 4.25 0 000-8.5z"
          clipRule="evenodd"
        ></path>
      </g>
    </svg>
  );
}

const GroupVerificationPage: React.FC = () => {
  const signupUser = useSignupUser();
  const navigate = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailAddress = queryParams.get('email_address') || '';

  const [groupIdValue, setGroupIdValue] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [inProgress, setInProgress] = useState<boolean>(false);

  const signup = (clientCode: string) =>
    signupUser.mutateAsync(
      { email_address: emailAddress, package_code: clientCode },
      {
        onSuccess: (userResponse: AxiosResponse<MemberSignupResponse>) => {
          sessionStorage.setItem('partial_member_id', JSON.stringify(userResponse.data.id));
          navigate.push({
            pathname: ROUTE_PATH.SIGNUP_EMAIL_VERIFICATION,
            search: `?email_address=${encodeURIComponent(emailAddress)}&mode=welcome`,
          });
        },
        onError: (error: any) => {
          setError(error.response.data.description);
        },
      },
    );

  const handleGroupVerification = async () => {
    try {
      setInProgress(true);
      setError(undefined);

      await isGroupSelfEnroll(
        groupIdValue,
        (isSelfEnroll) => {
          if (isSelfEnroll) {
            setSessionStorageItem('clientCode', groupIdValue);
            signup(groupIdValue);
          } else {
            setError('Provided package code is invalid.');
          }
        },
        (err) => {
          setError(err.response?.data.description);
        },
      );
    } finally {
      setInProgress(false);
    }
  };

  const handleBack = () => {
    navigate.push(ROUTE_PATH.SIGNUP);
  };

  return (
    <OnboardingLayout>
      <div className="mb-6 space-y-1">
        <h1 className="font-albert-sans text-xl font-semibold leading-[1.5] text-black">Confirm your organization</h1>
        <div className="font-open-sans space-y-0 text-sm font-normal leading-[1.5] text-black">
          <p className="mb-0">If you belong to an organization, confirm your Group ID below.</p>
          <p className="mb-0">&nbsp;</p>
          <p>If you do not know your Group ID and no ID is shown below, contact your plan administrator.</p>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-4">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-1">
            <label className="font-albert-sans text-label-gray text-sm font-medium leading-5">Group ID</label>
            <span className="text-error">*</span>
          </div>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <KeyIcon />
            </div>
            <input
              required
              id="group_id"
              type="text"
              value={groupIdValue}
              placeholder="Enter Group ID"
              onChange={(e) => setGroupIdValue(e.target.value)}
              className={`w-full rounded-xl border ${
                error ? 'border-error' : 'border-input-border-gray'
              } font-open-sans bg-gray-light placeholder:text-placeholder-gray focus:ring-button-primary py-[10px] pl-10 pr-3 text-sm leading-5 text-black focus:border-transparent focus:outline-none focus:ring-2`}
            />
          </div>
          {error && <p className="font-open-sans text-error text-sm leading-5">{error}</p>}
        </div>
      </div>

      {error && (
        <div className="mb-6 space-y-3 py-3">
          <p className="text-base leading-6 text-gray-900">
            If you're registered through an organization, be sure to use your email associated with that organization.
          </p>
          <p className="text-base leading-6 text-gray-900">
            You can also check with your organization's benefit manager to get your Group ID.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <button
          className="font-albert-sans bg-button-primary hover:bg-button-primary-hover w-full rounded-full border-none px-7 py-[10px] text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="verify-group-button"
          onClick={handleGroupVerification}
          disabled={inProgress}
        >
          {inProgress ? 'Verifying...' : 'Verify Group ID'}
        </button>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button onClick={handleBack} className="text-primary flex items-center gap-1.5 hover:cursor-pointer">
          <ArrowButton backgroundColor="rgb(var(--color-button-secondary))" direction="left" className="h-5 w-5" />
          <span className="font-albert-sans text-xs font-medium leading-6">Back</span>
        </button>
        <div className="flex items-center gap-4">
          <LoginButton />
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default GroupVerificationPage;
